import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://www.withcj.top/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 刷新相關狀態
let isRefreshing = false;
let refreshRequest = null; // 進行中的刷新請求 Promise
const requestQueue = [];

const processQueue = (error, token = null) => {
  while (requestQueue.length) {
    const { resolve, reject, config } = requestQueue.shift();
    if (error) {
      reject(error);
    } else {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      resolve(api(config));
    }
  }
};

// 請求攔截器：添加 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 如果是 FormData，讓瀏覽器自動設定 Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    console.log('API error:', {
      config: error.config,
      response: error.response,
      message: error.message,
    });
    return Promise.reject(error);
  }
);

// 響應攔截器：處理 token 過期，自動刷新並重試
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // 若無 response 或非 401，直接拋出
    if (!status || status !== 401) {
      return Promise.reject(error);
    }

    // 避免對刷新端點本身重入
    const isRefreshEndpoint = originalRequest?.url?.includes('/token/refresh/');
    if (isRefreshEndpoint) {
      // 刷新失敗，清理並導向登入
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 若正在刷新，將請求排隊等待
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        requestQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    // 嘗試使用 refresh token 刷新
    const refreshToken = localStorage.getItem('refresh');
    if (!refreshToken) {
      // 無 refresh，直接登出
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      isRefreshing = true;
      if (!refreshRequest) {
        refreshRequest = api.post('/token/refresh/', { refresh: refreshToken });
      }
      const refreshResponse = await refreshRequest;
      const newAccess = refreshResponse.data?.access;

      if (!newAccess) {
        throw new Error('No access token in refresh response');
      }

      // 更新本地與 axios 預設標頭
      localStorage.setItem('token', newAccess);
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

      // 重試原請求
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;

      // 處理排隊請求
      const retryPromise = api(originalRequest);
      processQueue(null, newAccess);
      return retryPromise;
    } catch (refreshError) {
      // 刷新失敗，清理狀態
      processQueue(refreshError, null);
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      localStorage.removeItem('user');
      delete api.defaults.headers.common['Authorization'];
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
      refreshRequest = null;
    }
  }
);

// 用戶相關的 API 函數
export const userApi = {
  createUser: async (userData) => {
    try {
      const response = await api.post('/users/', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getUsers: async (filter = '') => {
    try {
      const response = await api.get(`/users/${filter ? `?role=${filter}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/users/${userId}/`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteUser: async (userId) => {
    try {
      await api.delete(`/users/${userId}/`);
    } catch (error) {
      throw error;
    }
  }
};

// 評估相關的 API 函數
export const evaluationApi = {
  // 獲取可評估的預約記錄
  getAvailableBookings: async () => {
    const response = await api.get('/available-bookings/');
    return response.data;
  },

  // 標記預約評估為完成
  markBookingCompleted: async (bookingId, notes = '') => {
    const response = await api.post('/mark-booking-completed/', {
      booking_id: bookingId,
      notes: notes
    });
    return response.data;
  },

  // 標記預約評估為未完成
  markBookingIncomplete: async (bookingId) => {
    const response = await api.post('/mark-booking-incomplete/', {
      booking_id: bookingId
    });
    return response.data;
  }
};

export default api; 